import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { FiBell, FiCalendar, FiChevronDown, FiChevronLeft, FiChevronRight, FiEdit2, FiX } from "react-icons/fi";
import WalkInAlertsPill from "../../components/WalkInAlertsPill";
import toast from "react-hot-toast";

interface ApiDayConfig {
  date: string;
  day: string;
  isOpen: boolean;
  openingTime: string;
  closingTime: string;
  reason: string | null;
  wasManuallyClosed: boolean;
}

interface CalendarSummary {
  totalDays: number;
  openDays: number;
  closedDays: number;
  specialHours: number;
  emergencyClosures: number;
}

interface ApiDayAvailability {
  date: string;
  day: string;
  isOpen: boolean;
  status: string;
  openingTime: string;
  closingTime: string;
  reason: string | null;
  source: string;
}

export default function StoreCalendar() {
  
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [selectedDateStr, setSelectedDateStr] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = (now.getMonth() + 1).toString().padStart(2, "0");
    const d = now.getDate().toString().padStart(2, "0");
    return `${y}-${m}-${d}`;
  });

  const [daysMap, setDaysMap] = useState<Record<string, ApiDayConfig>>({});
  const [summary, setSummary] = useState<CalendarSummary | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedDayAvailability, setSelectedDayAvailability] = useState<ApiDayAvailability | null>(null);
  const [isDayLoading, setIsDayLoading] = useState(false);
  const [dayError, setDayError] = useState<string | null>(null);

  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [modalOpeningTime, setModalOpeningTime] = useState("09:00");
  const [modalClosingTime, setModalClosingTime] = useState("21:00");

  const [isEditingReason, setIsEditingReason] = useState(false);
  const [inlineReason, setInlineReason] = useState("");

  const getToken = () => {
    const auth = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    return jsonData?.token as string | undefined;
  };

  const authHeaders = (token?: string) =>
    token ? { Authorization: `Bearer ${token}` } : undefined;

  const fetchCalendar = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = getToken();
      const year = currentDate.getFullYear();
      const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
      const monthStr = `${year}-${month}`;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/calendar?month=${monthStr}`,
        { headers: authHeaders(token) }
      );

      if (res.data?.success) {
        const payload = res.data.data;
        setSummary(payload.summary ?? null);
        
        const newMap: Record<string, ApiDayConfig> = {};
        if (Array.isArray(payload.days)) {
          payload.days.forEach((day: ApiDayConfig) => {
            const dateKey = day.date.split("T")[0];
            newMap[dateKey] = day;
          });
        }
        setDaysMap(newMap);
      } else {
        setError("Unexpected calendar response format from server");
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Failed to fetch calendar";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [currentDate]);

  const fetchDayAvailability = useCallback(async (dateStr: string) => {
    setIsDayLoading(true);
    setDayError(null);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/calendar/availability?date=${dateStr}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setSelectedDayAvailability(res.data.data);
      } else {
        setDayError("Failed to fetch day details");
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Failed to fetch day availability";
      setDayError(msg);
    } finally {
      setIsDayLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  useEffect(() => {
    setIsEditingReason(false);
    fetchDayAvailability(selectedDateStr);
  }, [selectedDateStr, fetchDayAvailability]);

  // Retrieve day configuration with static fallbacks for safety
  const getDayInfo = useCallback((dateStr: string): ApiDayConfig => {
    if (daysMap[dateStr]) {
      return daysMap[dateStr];
    }
    
    // Default fallback based on day of week
    const parts = dateStr.split("-");
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    const dateObj = new Date(y, m, d);
    const dayOfWeek = dateObj.getDay(); // 0 = Sunday, 6 = Saturday

    const isOpenDefault = dayOfWeek !== 0; // Close Sundays by default
    return {
      date: `${dateStr}T00:00:00.000Z`,
      day: ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][dayOfWeek],
      isOpen: isOpenDefault,
      openingTime: "09:00",
      closingTime: isOpenDefault ? "21:00" : "00:00",
      reason: dayOfWeek === 0 ? "Weekly Closed Day" : null,
      wasManuallyClosed: false,
    };
  }, [daysMap]);

  const selectedDayInfo = useMemo(() => {
    if (selectedDayAvailability && selectedDayAvailability.date.split("T")[0] === selectedDateStr) {
      return {
        isOpen: selectedDayAvailability.isOpen,
        openingTime: selectedDayAvailability.openingTime,
        closingTime: selectedDayAvailability.closingTime,
        reason: selectedDayAvailability.reason,
      };
    }
    const fallback = getDayInfo(selectedDateStr);
    return {
      isOpen: fallback.isOpen,
      openingTime: fallback.openingTime,
      closingTime: fallback.closingTime,
      reason: fallback.reason,
    };
  }, [selectedDateStr, selectedDayAvailability, getDayInfo]);

  // Format 24h string to 12h AM/PM
  const formatTime = (time24: string) => {
    if (!time24 || time24 === "00:00") return "Closed";
    const parts = time24.split(":");
    if (parts.length < 2) return time24;
    const hrs = parseInt(parts[0], 10);
    const mins = parts[1];
    const ampm = hrs >= 12 ? "PM" : "AM";
    const displayHrs = hrs % 12 || 12;
    return `${displayHrs}:${mins} ${ampm}`;
  };

  
  const updateDayCalendar = async (params: {
    date: string;
    isOpen: boolean;
    openingTime?: string;
    closingTime?: string;
    reason?: string | null;
  }) => {
    const loadingToast = toast.loading("Updating calendar settings...");
    try {
      const token = getToken();
      
      const payload: any = {
        date: params.date,
        isOpen: params.isOpen,
      };
      if (params.openingTime) payload.openingTime = params.openingTime;
      if (params.closingTime) payload.closingTime = params.closingTime;
      if (params.reason !== undefined) payload.reason = params.reason;

      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/calendar/day`,
        payload,
        { headers: authHeaders(token) }
      );

      if (res.data?.success) {
        toast.success("Calendar settings updated successfully", { id: loadingToast });
        fetchCalendar(); // Refresh calendar settings from server
        fetchDayAvailability(params.date); // Refresh selected day availability from server
      } else {
        toast.error("Failed to update calendar settings", { id: loadingToast });
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Failed to update calendar settings";
      toast.error(msg, { id: loadingToast });
    }
  };

  // Toggle open/closed status for the selected date
  const handleToggleDayStatus = () => {
    const newStatus = !selectedDayInfo.isOpen;
    let currentReason = selectedDayInfo.reason;

    // Enforce reason when closing the day
    if (newStatus === false) {
      if (!currentReason || currentReason.trim() === "" || currentReason.toLowerCase() === "normal operating day") {
        toast.error("Please add a reason first before closing the day.");
        setIsEditingReason(true);
        return;
      }
    }

    updateDayCalendar({
      date: selectedDateStr,
      isOpen: newStatus,
      openingTime: selectedDayInfo.openingTime,
      closingTime: selectedDayInfo.closingTime,
      reason: currentReason,
    });
  };

  const handleEditOperatingHours = () => {
    if (!selectedDayInfo.isOpen) {
      toast.error("Cannot edit operating hours for a closed day");
      return;
    }
    setModalOpeningTime(selectedDayInfo.openingTime || "09:00");
    setModalClosingTime(selectedDayInfo.closingTime || "21:00");
    setIsTimeModalOpen(true);
  };

  const handleSaveTime = () => {
    updateDayCalendar({
      date: selectedDateStr,
      isOpen: selectedDayInfo.isOpen,
      openingTime: modalOpeningTime,
      closingTime: modalClosingTime,
      reason: selectedDayInfo.reason,
    });
    setIsTimeModalOpen(false);
  };

  const handleSaveInlineReason = () => {
    const finalReason = inlineReason.trim() === "" ? null : inlineReason.trim();
    
    // Block removing the reason if the day is closed
    if (!selectedDayInfo.isOpen && !finalReason) {
      toast.error("A reason must be provided for closed days.");
      return;
    }

    updateDayCalendar({
      date: selectedDateStr,
      isOpen: selectedDayInfo.isOpen,
      openingTime: selectedDayInfo.openingTime,
      closingTime: selectedDayInfo.closingTime,
      reason: finalReason,
    });
    setIsEditingReason(false);
  };

  // Compute active month grid days dynamically
  const gridDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth(); // 0-indexed

    // First day of the month
    const firstDay = new Date(year, month, 1);
    const firstWeekday = firstDay.getDay(); // 0 = Sun, 1 = Mon...

    // startOffset: padding days needed from previous month (MON to SUN alignment)
    const startOffset = (firstWeekday + 6) % 7;

    const list: { dayNum: number; dateStr: string; isCurrentMonth: boolean }[] = [];

    // 1. Fill previous month's padding days
    const prevMonthEnd = new Date(year, month, 0);
    const prevMonthDaysCount = prevMonthEnd.getDate();
    const prevMonthVal = month === 0 ? 11 : month - 1;
    const prevYearVal = month === 0 ? year - 1 : year;
    
    for (let i = startOffset - 1; i >= 0; i--) {
      const dayNum = prevMonthDaysCount - i;
      const dateStr = `${prevYearVal}-${(prevMonthVal + 1).toString().padStart(2, "0")}-${dayNum.toString().padStart(2, "0")}`;
      list.push({ dayNum, dateStr, isCurrentMonth: false });
    }

    // 2. Fill current month's days
    const currentMonthDaysCount = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= currentMonthDaysCount; i++) {
      const dateStr = `${year}-${(month + 1).toString().padStart(2, "0")}-${i.toString().padStart(2, "0")}`;
      list.push({ dayNum: i, dateStr, isCurrentMonth: true });
    }

    // 3. Fill next month's padding days (limit grid to 35 or 42 cells)
    const targetSize = list.length <= 35 ? 35 : 42;
    const paddingNeeded = targetSize - list.length;
    const nextMonthVal = month === 11 ? 0 : month + 1;
    const nextYearVal = month === 11 ? year + 1 : year;

    for (let i = 1; i <= paddingNeeded; i++) {
      const dateStr = `${nextYearVal}-${(nextMonthVal + 1).toString().padStart(2, "0")}-${i.toString().padStart(2, "0")}`;
      list.push({ dayNum: i, dateStr, isCurrentMonth: false });
    }

    return list;
  }, [currentDate]);

  // Month navigation helpers
  const handlePrevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleGoToday = () => {
    const today = new Date();
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDateStr(`${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`);
  };

  const handleExportCSV = () => {
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const monthStr = `${year}-${month}`;

    const headers = ["Date", "Day", "Status", "Opening Time", "Closing Time", "Manually Closed", "Reason"];
    
    // Sort all current days from daysMap
    const daysList = Object.entries(daysMap)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([dateKey, day]) => [
        dateKey,
        day.day,
        day.isOpen ? "Open" : "Closed",
        day.openingTime,
        day.closingTime,
        day.wasManuallyClosed ? "Yes" : "No",
        day.reason || "",
      ]);

    if (daysList.length === 0) {
      toast.error("No calendar data to export");
      return;
    }

    const csvContent = [
      headers.join(","),
      ...daysList.map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `store_calendar_report_${monthStr}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Store calendar report for ${monthStr} exported successfully`);
  };

  // Format selected date nicely for the top-right pill (e.g. "1 May, 2026")
  const formattedSelectedDate = useMemo(() => {
    try {
      const parts = selectedDateStr.split("-");
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const d = parseInt(parts[2], 10);
      const dateObj = new Date(y, m, d);
      return dateObj.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return selectedDateStr;
    }
  }, [selectedDateStr]);

  // Human readable month indicator
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const displayMonthStr = `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

  return (
    <div className="w-full min-h-screen p-4 xl:p-6 flex flex-col gap-6 font-sans">
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-gray-950">Store Calendar</h1>
          <p className="text-gray-400 text-sm mt-0.5">Operating schedule and special closures</p>
        </div>

        {/* Top-right Controls */}
        <div className="flex items-center gap-3 self-stretch md:self-auto flex-wrap">
          {/* Alerts Pill */}
          <WalkInAlertsPill />

          {/* Date Picker Pill */}
          <button className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-100 shadow-sm text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors">
            <FiCalendar className="w-4 h-4 text-[#008060]" />
            <span>{formattedSelectedDate}</span>
          </button>

          {/* Export Pill */}
          <button
            onClick={handleExportCSV}
            className="flex items-center justify-between gap-3 px-4 py-2 bg-[#008060] text-white rounded-lg text-sm font-semibold hover:bg-[#006b50] transition-colors shadow-sm min-w-[150px]"
          >
            <span>Export Summary</span>
            <FiChevronDown className="text-white" />
          </button>
        </div>
      </div>

      {/* Row of Metrics (4 cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Card 1: Open Days */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Open Days This Month</p>
          <div className="flex items-baseline gap-1.5 mt-2">
            {isLoading ? (
              <div className="h-7 w-12 bg-gray-100 rounded animate-pulse my-1" />
            ) : (
              <h3 className="text-[26px] font-black text-gray-900 leading-none">
                {summary?.openDays ?? 0}
              </h3>
            )}
            <span className="text-[#008060] text-sm font-bold select-none">↑</span>
          </div>
          <p className="text-gray-400 text-[11px] font-medium mt-1">This Month</p>
        </div>

        {/* Card 2: Closed Days */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Closed Days</p>
          <div className="flex items-baseline gap-1.5 mt-2">
            {isLoading ? (
              <div className="h-7 w-12 bg-gray-100 rounded animate-pulse my-1" />
            ) : (
              <h3 className="text-[26px] font-black text-gray-900 leading-none">
                {summary?.closedDays ?? 0}
              </h3>
            )}
            <span className="text-[#008060] text-sm font-bold select-none">↑</span>
          </div>
          <p className="text-gray-400 text-[11px] font-medium mt-1">This Month</p>
        </div>

        {/* Card 3: Special Hours */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Special Hours</p>
          {isLoading ? (
            <div className="h-7 w-16 bg-gray-100 rounded animate-pulse my-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">
              {summary?.specialHours ?? 0}
            </h3>
          )}
          <p className="text-gray-400 text-[11px] font-medium mt-1">This Month</p>
        </div>

        {/* Card 4: Emergency Closures */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Emergency Closures</p>
          {isLoading ? (
            <div className="h-7 w-16 bg-gray-100 rounded animate-pulse my-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">
              {summary?.emergencyClosures ?? 0} Days
            </h3>
          )}
          <p className="text-gray-400 text-[11px] font-medium mt-1">This Month</p>
        </div>
      </div>

      {/* Calendar Split View (Grid) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Side: Calendar Grid Panel */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100/50 p-5 flex flex-col gap-6">
          {/* Calendar Navigation header */}
          <div className="flex justify-between items-center">
            {/* Today Button */}
            <button 
              onClick={handleGoToday}
              className="px-4 py-1.5 border border-gray-200 text-gray-700 text-xs font-bold rounded-lg bg-white hover:bg-gray-50 transition-colors shadow-sm"
            >
              Today
            </button>

            {/* Navigation Month Indicator */}
            <div className="flex items-center gap-4">
              <button 
                onClick={handlePrevMonth}
                className="text-gray-400 hover:text-gray-600 transition-colors font-bold text-sm p-1"
                title="Previous Month"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-base font-bold text-gray-900 select-none min-w-[140px] text-center">
                {displayMonthStr}
              </span>
              <button 
                onClick={handleNextMonth}
                className="text-gray-400 hover:text-gray-600 transition-colors font-bold text-sm p-1"
                title="Next Month"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Calendar Table Grid */}
          {error ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 border border-dashed border-gray-200 rounded-2xl bg-gray-50/50">
              <p className="text-sm text-gray-500 font-medium mb-3">{error}</p>
              <button
                onClick={() => fetchCalendar()}
                className="px-4 py-2 bg-[#008060] text-white rounded-lg text-xs font-bold hover:bg-[#006b50] transition-colors shadow-sm"
              >
                Retry Load
              </button>
            </div>
          ) : (
            <div className="w-full flex flex-col border border-gray-100 rounded-xl overflow-hidden shadow-sm relative">
              {isLoading && (
                <div className="absolute inset-0 bg-white/40 flex items-center justify-center z-20 backdrop-blur-[0.5px]">
                  <div className="w-8 h-8 border-4 border-[#EAF5EF] border-t-[#008060] rounded-full animate-spin" />
                </div>
              )}

              {/* Columns header row */}
              <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-100 text-center py-2 text-[10px] font-bold text-gray-400 tracking-wider">
                <div>MON</div>
                <div>TUE</div>
                <div>WED</div>
                <div>THU</div>
                <div>FRI</div>
                <div>SAT</div>
                <div>SUN</div>
              </div>

              {/* Calendar Days Matrix */}
              <div className="grid grid-cols-7 text-gray-700 font-medium">
                {gridDays.map((day, idx) => {
                  const isSelected = selectedDateStr === day.dateStr;
                  const info = getDayInfo(day.dateStr);

                  // Set hatched style:
                  // - Out of month days get green stripe background
                  // - Closed days in the current month get red stripe background
                  let cellStyle: React.CSSProperties = {};
                  if (!day.isCurrentMonth) {
                    cellStyle = {
                      background: "repeating-linear-gradient(135deg, rgba(234,249,242,0.3), rgba(234,249,242,0.3) 8px, rgba(212,242,228,0.3) 8px, rgba(212,242,228,0.3) 16px)"
                    };
                  } else if (!info.isOpen) {
                    cellStyle = {
                      background: "repeating-linear-gradient(135deg, rgba(254,242,242,0.5), rgba(254,242,242,0.5) 8px, rgba(254,226,226,0.5) 8px, rgba(254,226,226,0.5) 16px)"
                    };
                  }

                  return (
                    <div
                      key={idx}
                      style={cellStyle}
                      onClick={() => setSelectedDateStr(day.dateStr)}
                      className={`border-b border-r border-gray-100/80 min-h-[90px] p-2 flex flex-col justify-between cursor-pointer transition-all hover:bg-gray-50/20 relative select-none ${
                        isSelected ? "ring-2 ring-[#008060] ring-inset bg-gray-50/30 z-10" : ""
                      } ${!day.isCurrentMonth ? "text-gray-300" : "text-gray-800"}`}
                    >
                      {/* Day number top right */}
                      <span className="self-end text-xs font-bold">{day.dayNum}</span>

                      {/* Event/Closure reason bar absolute positioned */}
                      {day.isCurrentMonth && (
                        <>
                          {!info.isOpen && (
                            <div className="w-[90%] left-[5%] bottom-2 absolute py-1 px-2 rounded text-[9px] font-bold select-none bg-[#FFF5F5] text-red-600 border-l-[3px] border-red-500 truncate">
                              {info.reason || "Closed"}
                            </div>
                          )}
                          {info.isOpen && info.reason && (
                            <div className="w-[90%] left-[5%] bottom-2 absolute py-1 px-2 rounded text-[9px] font-bold select-none bg-[#EAF2FD] text-blue-600 border-l-[3px] border-blue-500 truncate">
                              {info.reason}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Side: Day Details Drawer Panel */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100/50 p-5 flex flex-col justify-between min-h-[450px] relative">
          {isDayLoading && (
            <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 rounded-2xl backdrop-blur-[0.5px]">
              <div className="w-6 h-6 border-2 border-[#EAF5EF] border-t-[#008060] rounded-full animate-spin" />
            </div>
          )}

          <div className="flex flex-col gap-5">
            <div>
              <h2 className="text-base font-bold text-gray-950">Day Details</h2>
              <p className="text-xs text-gray-400 mt-1 pb-3 border-b border-gray-100">
                {new Date(selectedDateStr).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>

            {/* Status section */}
            <div className="flex flex-col gap-1 mt-1">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-xs font-semibold tracking-wide">Status</span>
                {/* Toggle switch */}
                <button
                  type="button"
                  onClick={handleToggleDayStatus}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                    selectedDayInfo.isOpen ? "bg-[#008060]" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200 ${
                      selectedDayInfo.isOpen ? "translate-x-5" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
              <span className={`text-sm font-bold mt-0.5 ${selectedDayInfo.isOpen ? "text-[#008060]" : "text-red-500"}`}>
                {selectedDayInfo.isOpen ? "Open" : "Closed"}
              </span>
            </div>

            {/* Operating Hours section */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-xs font-semibold tracking-wide">Operating Hours</span>
                <button 
                  onClick={handleEditOperatingHours}
                  className="text-[#008060] hover:text-[#006b50] transition-colors"
                >
                  <FiEdit2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <span className="text-gray-800 text-sm font-semibold mt-0.5">
                {selectedDayInfo.isOpen
                  ? `${formatTime(selectedDayInfo.openingTime)} - ${formatTime(selectedDayInfo.closingTime)}`
                  : "Closed"}
              </span>
            </div>

            {/* Reason section */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-xs font-semibold tracking-wide">Reason for closure / Opening</span>
                {!isEditingReason && (
                  <button 
                    onClick={() => {
                      setInlineReason(selectedDayInfo.reason || "");
                      setIsEditingReason(true);
                    }}
                    className="text-[#008060] hover:text-[#006b50] transition-colors"
                  >
                    <FiEdit2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              
              {isEditingReason ? (
                <div className="flex flex-col gap-2 mt-1">
                  <textarea
                    value={inlineReason}
                    onChange={(e) => setInlineReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#008060]/20 focus:border-[#008060] resize-none"
                    placeholder="Enter reason..."
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setIsEditingReason(false)}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-[11px] font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveInlineReason}
                      className="px-3 py-1.5 bg-[#008060] text-white rounded-lg text-[11px] font-semibold hover:bg-[#006b50] transition-colors shadow-sm"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <span className="text-gray-500 text-sm mt-0.5 leading-relaxed">
                  {selectedDayInfo.reason || "Normal operating day"}
                </span>
              )}
            </div>
          </div>

          {/* Close the Day Button */}
          <button
            onClick={handleToggleDayStatus}
            className="w-full py-3 bg-[#008060] hover:bg-[#006b50] text-white text-sm font-bold rounded-lg shadow-sm transition-colors mt-8 select-none"
          >
            {selectedDayInfo.isOpen ? "Close the Day" : "Open the Day"}
          </button>
        </div>
      </div>

      {/* Edit Time Modal */}
      {isTimeModalOpen && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl flex flex-col gap-5 border border-gray-100/50 animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="flex justify-between items-center pb-2 border-b border-gray-100">
              <div>
                <h3 className="text-base font-bold text-gray-900">Edit Time</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(selectedDateStr).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
              <button
                onClick={() => setIsTimeModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Time Picker Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Opening Time</label>
                <input
                  type="time"
                  value={modalOpeningTime}
                  onChange={(e) => setModalOpeningTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#008060]/20 focus:border-[#008060]"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Closing Time</label>
                <input
                  type="time"
                  value={modalClosingTime}
                  onChange={(e) => setModalClosingTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#008060]/20 focus:border-[#008060]"
                />
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={() => setIsTimeModalOpen(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTime}
                className="px-5 py-2 bg-[#008060] hover:bg-[#006b50] text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
              >
                Save Time
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
